import { pdf, Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import html2canvas from 'html2canvas';

// Styles globaux
const styles = StyleSheet.create({
    page: {
        backgroundColor: '#ffffff',
    },
});

// Composant Document PDF
const MyDocument = ({ elements, width, height }) => (
    <Document>
        <Page size={[width, height]} style={styles.page}>
            {elements.map((el) => {
                // Style commun pour le positionnement absolu
                const commonStyle = {
                    position: 'absolute',
                    left: el.x,
                    top: el.y,
                    width: el.width,
                    height: el.height,
                    opacity: el.opacity !== undefined ? el.opacity : 1.0,
                };

                if (el.type === 'text') {
                    return (
                        <Text
                            key={el.id}
                            style={{
                                ...commonStyle,
                                fontSize: el.fontSize || 14,
                                fontFamily: 'Helvetica',
                                textAlign: el.textAlign || 'left',
                                fontWeight: el.fontWeight === 'bold' ? 'bold' : 'normal',
                                fontStyle: el.fontStyle === 'italic' ? 'italic' : 'normal',
                                textDecoration: el.textDecoration === 'underline' ? 'underline' : 'none',
                                color: el.color || '#000000',
                            }}
                        >
                            {el.content || 'Texte'}
                        </Text>
                    );
                }

                if (el.type === 'image') {
                    if (!el.imageSrc) {
                        return (
                            <View
                                key={el.id}
                                style={{
                                    ...commonStyle,
                                    borderWidth: 1,
                                    borderColor: '#4CAF50',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Text style={{ fontSize: 12, color: '#4CAF50' }}>
                                    [Image - No Media]
                                </Text>
                            </View>
                        );
                    }

                    return (
                        <Image
                            key={el.id}
                            src={el.imageSrc}
                            style={{
                                ...commonStyle,
                                objectFit: 'cover'
                            }}
                        />
                    );
                }

                if (el.type === 'chart') {
                    // Si on a réussi à capturer l'image du chart
                    if (el.chartImage) {
                        return (
                            <Image
                                key={el.id}
                                src={el.chartImage}
                                style={commonStyle}
                            />
                        );
                    }
                    // Fallback si pas d'image (ex: pas de query)
                    return (
                        <View
                            key={el.id}
                            style={{
                                ...commonStyle,
                                borderWidth: 2,
                                borderColor: '#2196F3',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Text style={{ fontSize: 12, color: '#2196F3' }}>
                                {el.queryId ? '[Chart Loading Error]' : '[Chart - No Query]'}
                            </Text>
                        </View>
                    );
                }

                if (el.type === 'colorbloc') {
                    return (
                        <View
                            key={el.id}
                            style={{
                                ...commonStyle,
                                backgroundColor: el.backgroundColor || '#2196F3'
                            }}
                        />
                    );
                }

                return null;
            })}
        </Page>
    </Document>
);

export const generatePDF = async (elements, canvasRef, elementRefs, CANVAS_WIDTH, CANVAS_HEIGHT) => {
    // Activer le mode export PDF pour masquer les éléments superflus (boutons suppression, guides...)
    const canvas = canvasRef.current;
    if (canvas) {
        canvas.classList.add('pdf-export-mode');
    }

    // Attendre que le rendu visuel soit stable
    await new Promise(resolve => setTimeout(resolve, 500));

    // Préparer les éléments (notamment capturer les charts)
    const processedElements = await Promise.all(elements.map(async (element) => {
        // Copie de l'élément pour ne pas muter l'état original
        const newEl = { ...element };

        if (element.type === 'chart' && element.queryId) {
            try {
                const chartElement = elementRefs.current[element.id];
                if (chartElement) {
                    const allCanvases = chartElement.querySelectorAll('canvas');

                    if (allCanvases.length > 0) {
                        if (allCanvases.length === 1) {
                            newEl.chartImage = allCanvases[0].toDataURL('image/png');
                        } else {
                            // Combiner plusieurs canvas (ex: amCharts avec légende)
                            const sortedCanvases = Array.from(allCanvases).sort((a, b) =>
                                (b.width * b.height) - (a.width * a.height)
                            );
                            const mainCanvas = sortedCanvases[0];

                            const compositeCanvas = document.createElement('canvas');
                            compositeCanvas.width = mainCanvas.width;
                            compositeCanvas.height = mainCanvas.height;
                            const ctx = compositeCanvas.getContext('2d');
                            ctx.imageSmoothingEnabled = false;

                            const containerRect = chartElement.getBoundingClientRect();

                            allCanvases.forEach(cvs => {
                                const rect = cvs.getBoundingClientRect();
                                const relX = rect.left - containerRect.left;
                                const relY = rect.top - containerRect.top;

                                const targetRatio = mainCanvas.width / containerRect.width;
                                const targetX = relX * targetRatio;
                                const targetY = relY * targetRatio;

                                ctx.drawImage(cvs, targetX, targetY, cvs.width, cvs.height);
                            });

                            newEl.chartImage = compositeCanvas.toDataURL('image/png');
                        }
                    } else {
                        // Fallback html2canvas
                        const chartWrapper = chartElement.querySelector('.chart-content-wrapper');
                        const elementToCapture = chartWrapper || chartElement;
                        const canvas = await html2canvas(elementToCapture, {
                            scale: 2,
                            backgroundColor: '#ffffff',
                            logging: false,
                            useCORS: true
                        });
                        newEl.chartImage = canvas.toDataURL('image/png');
                    }
                }
            } catch (error) {
                console.error('Erreur capture chart:', error);
            }
        }
        return newEl;
    }));

    try {
        // Générer le PDF avec @react-pdf/renderer
        const blob = await pdf(
            <MyDocument
                elements={processedElements}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
            />
        ).toBlob();

        // Télécharger
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'document.pdf';
        link.click();
        URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erreur génération PDF:', error);
    } finally {
        // Désactiver le mode export
        if (canvas) {
            canvas.classList.remove('pdf-export-mode');
        }
    }
};
